import { Vector, Circle, Polygon, Box } from 'sat'
import { flatten, minBy, maxBy } from 'lodash'
import { makeSatPolygons, VectorXY } from '../../utils/math'
import { wetzls } from '../../utils/wetzls'
import { ColorUtil } from '../../utils/colors'
import { radians, gameUnits } from '../../utils/primitive-types'
import { Bounds } from './bounds'

// unfortunately, type has no type-safety, so these are more dangerous than they should be
export type SatCollider = Circle | Polygon | Box | Ellipse
//export type ColliderWithTraits = (CircleWithTraits | PolygonWithTraits | BoxWithTraits | EllipseWithTraits)
interface ColliderWithTraits {
	type: ColliderType
	pos: Vector
	offsetPos: Vector
	disabled?: boolean
}

// SAT does not have ellipse so we define it here
export class Ellipse {
	pos: Vector
	rX: number
	rY: number
	constructor(pos: Vector, rX: number, rY: number) {
		this.pos = pos
		this.rX = rX
		this.rY = rY
	}
}

export class Line {
	pos: Vector
	pos2: Vector
	constructor(pos: Vector, p2: Vector) {
		this.pos = pos
		this.pos2 = p2
	}
}

export class Capsule {
	pos: Vector
	pos2: Vector
	r: number
	constructor(pos: Vector, p2: Vector, r: number) {
		this.pos = pos
		this.pos2 = p2
		this.r = r
	}
}

export enum ColliderType {
	Circle,
	Ellipse,
	Box,
	Polygon,
	Capsule,
	Lookup,
}
export interface ColliderConfig {
	type: ColliderType
	position: gameUnits[]
	/** angle in radians - only 'box' supported right now */
	angle?: radians
}

export interface CircleColliderConfig extends ColliderConfig {
	type: ColliderType.Circle
	radius: number
}

export interface PolygonColliderConfig extends ColliderConfig {
	type: ColliderType.Polygon
	vertices: number[]
}

export interface BoxColliderConfig extends ColliderConfig {
	type: ColliderType.Box
	width: number
	height: number
}

export interface EllipseColliderConfig extends ColliderConfig {
	type: ColliderType.Ellipse
	rX: number
	rY: number
}

export interface LookupColliderConfig extends ColliderConfig {
	type: ColliderType.Lookup
}

export type AnyColliderConfig = CircleColliderConfig | PolygonColliderConfig | BoxColliderConfig | EllipseColliderConfig | LookupColliderConfig
export type AnyCollider = CircleCollider | PolygonCollider | BoxCollider | EllipseCollider | CapsuleCollider

export class CircleCollider extends Circle implements ColliderWithTraits {
	type: ColliderType.Circle = ColliderType.Circle
	disabled?: boolean
	prevPosition?: Vector
	ignorePropThisFrame?: boolean
	offsetPos: Vector

	constructor(pos?: Vector, r?: number, offsetX?: number, offsetY?: number) {
		super(pos, r)

		this.offsetPos = new Vector(offsetX, offsetY)
	}
}

export class PolygonCollider extends Polygon implements ColliderWithTraits {
	type: ColliderType.Polygon = ColliderType.Polygon
	disabled?: boolean
	boundingCircle: Circle
	offsetPos: Vector

	constructor(pos?: Vector, points?: Vector[], offsetX?: number, offsetY?: number) {
		super(pos, points)
		const boundingCircle = wetzls(points)
		this.boundingCircle = new Circle(new Vector(boundingCircle.x + pos.x, boundingCircle.y + pos.y), boundingCircle.r)

		this.offsetPos = new Vector(offsetX, offsetY)
	}
}
export class BoxCollider extends Box implements ColliderWithTraits {
	/** The box is inscribed with a polygon where collision and intersection checks would be more efficient. */
	polygon: Polygon
	type: ColliderType.Box = ColliderType.Box
	disabled?: boolean
	angle: radians
	offsetPos: Vector

	originalAngle: number

	constructor(pos?: Vector, width?: number, height?: number, angle?: number, offsetX?: number, offsetY?: number) {
		super(pos, width, height)
		this.angle = angle || 0
		const points = [new Vector(0, -(height/2)), new Vector(width, -(height/2)), new Vector(width, height/2), new Vector(0, height/2)]

		this.polygon = new Polygon(pos, points)
		if (angle) {
			this.polygon.setAngle(angle)
			this.originalAngle = angle
		} else {
			this.originalAngle = 0
		}

		this.offsetPos = new Vector(offsetX, offsetY)
	}

	setAngle(angle: radians, keepOriginalAngle?: boolean) {
		this.angle = angle
		if (keepOriginalAngle) {
			this.angle += this.originalAngle
		}
		
		this.polygon.setAngle(this.angle)
	}
}

export class EllipseCollider extends Ellipse implements ColliderWithTraits {
	disabled?: boolean
	type: ColliderType.Ellipse = ColliderType.Ellipse
	offsetPos: Vector

	constructor(pos: Vector, rX: number, rY: number, offsetX?: number, offsetY?: number) {
		super(pos, rX, rY)

		this.offsetPos = new Vector(offsetX, offsetY)
	}
}

export class CapsuleCollider extends Capsule implements ColliderWithTraits {
	type: ColliderType.Capsule = ColliderType.Capsule
	disabled?: boolean
	offsetPos: Vector

	constructor(pos: Vector, p2: Vector, r: number, offsetX?: number, offsetY?: number) {
		super(pos, p2, r)

		this.offsetPos = new Vector(offsetX, offsetY)
	}
}


/* tslint:enable:no-bitwise */

// returns *array* of colliders, most configs have just 1 entry
//  polygon is one
export function createSubColliders(position: VectorXY, colliderConfig: AnyColliderConfig): AnyCollider[] {
	const posArray = colliderConfig.position
	const pos = new Vector(posArray[0] + position.x, posArray[1] + position.y)
	switch (colliderConfig.type) {
		case ColliderType.Circle: {
			const circle = new CircleCollider(pos, colliderConfig.radius, posArray[0], posArray[1])
			return [circle]
		}
		case ColliderType.Ellipse: {
			const ellipse = new EllipseCollider(pos, colliderConfig.rX, colliderConfig.rY, posArray[0], posArray[1])
			// TODO2: capsule (ie projectiles) VS ellipse doesn't work. We have a lot of these, see testCapsuleEllipse for problem
			return [ellipse]
		}
		case ColliderType.Box: {
			const box = new BoxCollider(pos, colliderConfig.width, colliderConfig.height, colliderConfig.angle, posArray[0], posArray[1])
			return [box]
		}
		case ColliderType.Polygon: {
			const polygons = makeSatPolygons(position, colliderConfig.vertices) //@TODO set offset with posArray so these can be rotated
			return polygons
		}
	}
}

export function updateColliderPositions(colliderConfigs: AnyColliderConfig[], colliders: ColliderWithTraits[], ownerPos: VectorXY) {
	for (let i = 0; i < colliders.length; i++) {
		const collider = colliders[i]
		collider.pos.x = ownerPos.x + collider.offsetPos.x
		collider.pos.y = ownerPos.y + collider.offsetPos.y
	}
}

export function createColliders(position: VectorXY, colliderConfigs: AnyColliderConfig[]): AnyCollider[] {
	return flatten(colliderConfigs.map((cc) => createSubColliders(position, cc)))
}

export function collidersBounds(colliders: AnyCollider[]): Bounds {
	const bounds = new Bounds()
	colliders.forEach((collider) => {
		const b = colliderBounds(collider)
		bounds.addBounds(b)
	})
	return bounds
}

export function colliderBounds(collider: AnyCollider): Bounds {
	const pos = collider.offsetPos
	const bounds = new Bounds()
	switch (collider.type) {
		case ColliderType.Circle: {
			const circle = collider as Circle
			bounds.minX = pos.x - circle.r
			bounds.maxX = pos.x + circle.r
			bounds.minY = pos.y - circle.r
			bounds.maxY = pos.y + circle.r
			break
		}
		case ColliderType.Ellipse: {
			const ellipse = collider as Ellipse
			bounds.minX = pos.x - ellipse.rX
			bounds.maxX = pos.x + ellipse.rX
			bounds.minY = pos.y - ellipse.rY
			bounds.maxY = pos.y + ellipse.rY
			break
		}
		case ColliderType.Box: {
			const box = collider as BoxCollider
			const corners = box.polygon.calcPoints
			bounds.minX = minBy(corners, (c) => c.x).x
			bounds.maxX = maxBy(corners, (c) => c.x).x
			bounds.minY = minBy(corners, (c) => c.y).y
			bounds.maxY = maxBy(corners, (c) => c.y).y
			break
		}
		default: {
			throw new Error(`unhandled collider type: ${collider.type}`)
		}
	}

	return bounds
}

// assert that the collider type has the correct members
export function checkColliderConfig(colliderConfig: ColliderConfig, name: string) {
	switch (colliderConfig.type) {
		case ColliderType.Circle: {
			const circleConfig = colliderConfig as CircleColliderConfig
			console.assert(circleConfig.radius !== undefined, `no radius in ${name}`)
			break
		}
		case ColliderType.Ellipse: {
			const ellipseConfig = colliderConfig as EllipseColliderConfig
			console.assert(ellipseConfig.rX !== undefined, `no rX in ${name}`)
			console.assert(ellipseConfig.rY !== undefined, `no rY in ${name}`)
			break
		}
		case ColliderType.Box: {
			const boxConfig = colliderConfig as BoxColliderConfig
			console.assert(boxConfig.width !== undefined, `no width in ${name}`)
			console.assert(boxConfig.height !== undefined, `no height in ${name}`)
			break
		}
		case ColliderType.Polygon: {
			const polygonConfig = colliderConfig as PolygonColliderConfig
			console.assert(polygonConfig.vertices !== undefined, `no vertices in ${name}`)
			break
		}
		default: {
			throw new Error(`unhandled collider type: ${colliderConfig.type}`)
		}
	}
}

export function setPolygonFromBox(b: BoxCollider, polygon: Polygon, points: Vector[]) {
	polygon.pos = b.pos
	const w = b.w
	const h = b.h
	points[0].x = 0
	points[0].y = 0
	points[1].x = w
	points[1].y = 0
	points[2].x = w
	points[2].y = h
	points[3].x = 0
	points[3].y = h
	const boxAngle = (b as any).angle
	if (boxAngle) {
		polygon.setAngle(boxAngle)
	}
	polygon.setPoints(points)
}
